import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Badge, Text } from '@mantine/core';
import { IconTemplate } from '@tabler/icons-react';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileTemplates } from '@/features/templates/mobile/MobileTemplates';
import { categoryName, type TemplateRow } from '@/features/templates/api';

export const Route = createFileRoute('/_app/templates/')({ component: TemplatesRoute });

/** Selettore: lista template mobile (MaeDesign) vs tabella desktop. */
function TemplatesRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileTemplates /> : <TemplatesListPage />;
}

function TemplatesListPage() {
  const navigate = useNavigate();
  return (
    <MaeShell>
      <MaePageHead
        icon={<IconTemplate size={22} />}
        tone="azzurro"
        title="Template"
        sub="Template per email e documenti generati dal sistema."
      />
    <ResourceListPage<TemplateRow>
      title=""
      type="templates"
      columns={[
        {
          accessor: 'name',
          title: 'Nome',
          render: (row) => (
            <div>
              <Text fw={500}>{row.name}</Text>
              {row.objectText && <Text size="xs" c="dimmed">{row.objectText}</Text>}
            </div>
          ),
        },
        /* `category` è una relazione (non attributo): sort/filtro server-side → 400. E si legge
           con `nomeCategoria`, non direttamente: il valore è `{ id, name }`, e stamparlo nel JSX
           faceva cadere React su ogni riga che una categoria ce l'aveva. Prima non si vedeva
           perché `cell.getValue<string>()` prometteva una stringa che non c'era mai stata. */
        { accessor: 'category', title: 'Categoria', sortable: false, filterable: false, render: (row) => <Badge variant="light">{categoryName(row) || '—'}</Badge> },
        { accessor: 'language', title: 'Lingua' },
        {
          /*
           * `erasable === false` = template di sistema: lo manda un flusso del server e il titolo
           * e' protetto. Va detto **nell'elenco** e non solo dentro, perche' e' l'informazione che
           * decide se conviene aprirlo per rinominarlo — e sono 18 righe su 112.
           */
          accessor: 'erasable',
          title: 'Tipo',
          width: 120,
          sortable: false,
          filterable: false,
          render: (row) =>
            row.erasable === false
              ? <Badge variant="light" color="bordeaux">Di sistema</Badge>
              : <Badge variant="outline" color="gray">Personalizzato</Badge>,
        },
        {
          // L'attributo reale è `active`: `isActive` non esiste sulla risorsa, quindi questa
          // colonna mostrava «Inattivo» su ogni riga.
          accessor: 'active',
          title: 'Stato',
          sortable: false,
          filterable: false,
          render: (row) =>
            row.active
              ? <Badge color="green">Attivo</Badge>
              : <Badge color="gray">Inattivo</Badge>,
        },
      ]}
      searchField="name"
      onRowClick={(t) => void navigate({ to: `/templates/${t.id}` })}
      emptyMessage="Nessun template"
      emptyDescription="I template per email e documenti generati dal sistema appariranno qui."
      emptyIcon={IconTemplate}
    />
    </MaeShell>
  );
}
